import {Stack, Typography} from '@mui/material';
import {StyledGrid} from 'presentation/components';
import {SkeletonData} from 'presentation/components/ChangeData/SkeletonData';
import {
	AddressField,
	AddressLoading,
	BeneficiaryCard,
	CellPhoneField,
	EmailField,
	FacialBiometricAlreadyRegisteredModal,
} from 'presentation/pages/private/profile/components';
import {translations} from 'presentation/translations-new';
import React, {Fragment, Suspense, useEffect} from 'react';
import {useAccountInformation, useChangeDataProfile} from '../../hooks';
import BusinessToggle from '../components/BusinessToggle';
import {FormDrawerOrInline} from '../components/FormDrawerOrInline';
import {ProfileMenuItem} from '../components/ProfileMenuItem/ProfileMenuItem';
import {useProfilePage} from '../hooks/useProfilePage';
import {AccordionStateEnum} from '../store/formAccordionStore/types';
import {PROFILE_SECTION} from '../store/useProfileSection/constants';
import {CustomBox} from './styles';
import {useHostName} from 'presentation/hooks';
import {Bank} from 'presentation/components/ChangeData/Bank';
import {useQueryClient} from '@tanstack/react-query';
import {GetProgressResponse} from 'domain/usecases/private/profile/get-progress';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {PROFILE_PROGRESS_THRESHOLDS} from 'presentation/components/MainHeader/ProfileMenu/components/CompleteProfileCard/constants';
import {getPendingPercentage} from 'presentation/utils/getPendingPercentage';
import {useShowFacialBiometric} from '../../hooks/useShowFacialBiometric';
import {FavoriteAddressTutorials} from '../FavoriteAddresses/components/FavoriteAddressTutorials';
import {FAVORITE_ADDRESS_TUTORIAL_STEPS} from '../FavoriteAddresses/components/FavoriteAddressTutorials/constants';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useAccountDataStore} from '../../store/useAccountDataStateStore';

const {changeData} = translations['pt-br'].privateFlow.profile;
const {loginAndPrivacy, registerChange} = translations['pt-br'].others.profileMenu;

export function ProfileContent() {
	const queryClient = useQueryClient();
	const {
		isResponsibleWithoutBenefits,
		isB2BAccess,
		accesses,
		isHolder,
		odontoOnly,
		canChangePassword,
		canChangeAddress,
		canChangeAccess,
	} = useAccountInformation();
	const {isGndiSaoPaulo} = useHostName();

	const {
		beneficiary: {idadeC},
	} = useAccountDataStore(state => state.accountData);

	const isUnderage = idadeC < 18;

	const {changeDataFormState, onCancel, handleChangeDataFormOpen} = useChangeDataProfile();

	const {isEnabled: isMultiAddressEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_MULTI_ADDRESS,
	);

	const showBankData = accesses.refund && isHolder && !odontoOnly && isGndiSaoPaulo;
	const showFacialBiometric = useShowFacialBiometric();

	const {
		theme,
		isTablet,
		isLaptop,
		isMobile,
		activeSection,
		setActiveSection,
		REGISTRATION_DATA_ITEMS,
		LOGIN_AND_PRIVACY_ITEMS,
		showB2BToggle,
		isAlreadyRegisteredModalOpen,
		setIsAlreadyRegisteredModalOpen,
		isFavoriteAddressesFetched,
	} = useProfilePage();

	const progressData: GetProgressResponse | undefined = queryClient.getQueryData([
		QUERY_KEYS.PROGRESS_PROFILE,
		showBankData,
		canChangeAddress,
		showFacialBiometric,
	]);

	const pendingPercentage = getPendingPercentage({
		totalFields: progressData?.totalFields || 0,
		filledFields: progressData?.filledFields || 0,
	});

	const showLoginAndPrivacy =
		(isGndiSaoPaulo && isB2BAccess) || canChangePassword || canChangeAccess;

	const isCellPhoneActive =
		changeDataFormState.name === AccordionStateEnum.CELLPHONE &&
		changeDataFormState.isFormActive;

	const isEmailActive =
		changeDataFormState.name === AccordionStateEnum.EMAIL && changeDataFormState.isFormActive;

	const isAddressActive =
		changeDataFormState.name === AccordionStateEnum.ADDRESS && changeDataFormState.isFormActive;

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const cep = urlParams.get('cep');
		let timer: NodeJS.Timeout;
		if (cep) {
			timer = setTimeout(() => {
				REGISTRATION_DATA_ITEMS.find(i => i.id === 'address')?.onClick?.();
			}, 0);
		}

		return () => {
			if (timer) {
				clearTimeout(timer);
			}
		};
	}, []);

	return (
		<>
			<StyledGrid.Root>
				{!isTablet || !isLaptop ? (
					<StyledGrid.Item sm={12} md={12} lg={4}>
						<BeneficiaryCard
							isResponsibleWithoutBenefits={isResponsibleWithoutBenefits}
						/>

						<Stack
							sx={{
								backgroundColor: theme.palette.background.default,
								p: 2,
								gap: 3,
								borderRadius: 1.5,
								mt: 2,
							}}>
							<ProfileMenuItem
								onClick={() => {
									setActiveSection(PROFILE_SECTION.REGISTRATION_DATA);
									onCancel();
								}}
								label={registerChange}
								iconName="person-outline"
								showErrorIcon={pendingPercentage < PROFILE_PROGRESS_THRESHOLDS.MAX}
							/>
							{showLoginAndPrivacy ? (
								<ProfileMenuItem
									onClick={() => {
										setActiveSection(PROFILE_SECTION.LOGIN_AND_PRIVACY);
										onCancel();
									}}
									label={loginAndPrivacy}
									iconName="shield-encrypted"
								/>
							) : null}
						</Stack>
					</StyledGrid.Item>
				) : null}

				<StyledGrid.Item sm={6} md={6} lg={4}>
					<Stack spacing={1}>
						{activeSection === PROFILE_SECTION.REGISTRATION_DATA ? (
							<Fragment>
								{!isMobile ? (
									<Typography
										variant="body1"
										fontWeight={600}
										color="text.primary">
										{registerChange}
									</Typography>
								) : null}

								<Stack
									sx={{
										backgroundColor: theme.palette.background.paper,
										borderRadius: 2,
										p: 2,
										gap: 3,
									}}>
									{REGISTRATION_DATA_ITEMS.filter(i => i.shouldShow).map(item => (
										<ProfileMenuItem key={item.id} {...item} />
									))}
								</Stack>
								<FavoriteAddressTutorials
									step={FAVORITE_ADDRESS_TUTORIAL_STEPS.PROFILE_MENU}
									isBlocked={
										!isMultiAddressEnabled ||
										isUnderage ||
										!isFavoriteAddressesFetched
									}
								/>
							</Fragment>
						) : null}

						{activeSection === PROFILE_SECTION.LOGIN_AND_PRIVACY ? (
							<Fragment>
								{!isMobile ? (
									<Typography
										variant="body1"
										fontWeight={600}
										color="text.primary">
										{loginAndPrivacy}
									</Typography>
								) : null}

								<Stack
									sx={{
										backgroundColor: theme.palette.background.paper,
										borderRadius: 2,
										p: 2,
										gap: 3,
									}}>
									{LOGIN_AND_PRIVACY_ITEMS.filter(i => i.shouldShow).map(item => (
										<ProfileMenuItem key={item.id} {...item} />
									))}

									{showB2BToggle ? <BusinessToggle /> : null}
								</Stack>
							</Fragment>
						) : null}
					</Stack>
				</StyledGrid.Item>

				<StyledGrid.Item xs={12} sm={6} md={isLaptop ? 6 : 4}>
					<FormDrawerOrInline
						title={changeData.contact.titleCellPhone}
						open={isCellPhoneActive}
						isMobile={isMobile}
						onClose={() => onCancel(AccordionStateEnum.CELLPHONE)}>
						<CustomBox>
							<Suspense fallback={<SkeletonData type="cellPhone" />}>
								<CellPhoneField />
							</Suspense>
						</CustomBox>
					</FormDrawerOrInline>

					<FormDrawerOrInline
						title={changeData.contact.titleEmail}
						open={isEmailActive}
						isMobile={isMobile}
						onClose={() => onCancel(AccordionStateEnum.EMAIL)}>
						<CustomBox>
							<Suspense fallback={<SkeletonData type="email" />}>
								<EmailField />
							</Suspense>
						</CustomBox>
					</FormDrawerOrInline>

					<FormDrawerOrInline
						title={changeData.address.title}
						open={isAddressActive}
						isMobile={isMobile}
						onClose={() => onCancel(AccordionStateEnum.ADDRESS)}>
						<CustomBox>
							<Suspense fallback={<AddressLoading />}>
								<AddressField />
							</Suspense>
						</CustomBox>
					</FormDrawerOrInline>

					<FormDrawerOrInline
						title={changeData.bank.cardTitle}
						open={changeDataFormState.name === AccordionStateEnum.BANK}
						isMobile={isMobile}
						onClose={() => onCancel(AccordionStateEnum.BANK)}>
						<CustomBox>
							<Bank
								hiddenTitle
								onEmptyAction={handleChangeDataFormOpen}
								handleChangeForm={handleChangeDataFormOpen}
								showForm={changeDataFormState.isFormActive}
							/>
						</CustomBox>
					</FormDrawerOrInline>
				</StyledGrid.Item>
			</StyledGrid.Root>

			<FacialBiometricAlreadyRegisteredModal
				open={isAlreadyRegisteredModalOpen}
				onClose={() => setIsAlreadyRegisteredModalOpen(false)}
			/>
		</>
	);
}
